import React from 'react';
import { CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import CTASection from '../../components/CTASection';
import ChaosToOrder from '../../components/ChaosToOrder';
import SEO from '../../components/SEO';
import GradientOverlay from '../../components/GradientOverlay';
import LogomarkPattern from '../../components/LogomarkPattern';

const AIEnablement = () => {
  const useCases = [
    {
      title: 'Family-Owned Logistics Company',
      location: 'Brisbane',
      description: 'Undertakes an enablement programme to understand how AI can optimise delivery routes and warehouse management, resulting in 20% efficiency gains within 3 months.'
    },
    {
      title: 'Group of Financial Advisors',
      location: 'Singapore',
      description: 'Learns how to use AI tools to automate client reporting and identify personalised investment opportunities, allowing them to serve 40% more clients without hiring.'
    },
    {
      title: 'Growing Manufacturing Firm',
      location: 'Indonesia',
      description: 'Leadership team participates in a workshop to develop a strategic roadmap for implementing AI in their production line for quality control, reducing defects by 65%.'
    }
  ];

  const costsOfInaction = [
    'Falling behind competitors who are leveraging AI',
    'Operational inefficiencies that AI could easily solve',
    'Missing out on data-driven opportunities',
    'Making poor technology investments based on hype rather than strategy',
    'Inability to attract and retain top talent who want to work with modern tools'
  ];

  return (
    <div style={{ marginTop: '80px' }}>
      <SEO 
        title="AI Systems & Enablement Gold Coast"
        description="AI enablement programmes for Gold Coast businesses. Practical artificial intelligence solutions that deliver real ROI for Australian SMEs and scale-ups."
        keywords="AI Systems Gold Coast, AI Consultant Gold Coast, Artificial Intelligence Australia, AI Implementation Brisbane, Machine Learning Queensland, AI Business Solutions Gold Coast, AI Strategy Australia"
        canonical="https://resolvdigital.com.au/services/ai-enablement"
      />
      
      {/* Hero Section - DARK with Background Image */}
      <section style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#0a4447',
        backgroundImage: 'url(https://images.unsplash.com/photo-1542744173-8e7e53415bb0)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        padding: '120px 40px 80px'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(10, 68, 71, 0.92)'
        }} />
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ maxWidth: '900px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: 'rgba(13, 115, 119, 0.2)',
              border: '1px solid #0D7377',
              borderRadius: '10rem',
              padding: '8px 20px',
              marginBottom: '24px'
            }}>
              <Sparkles size={20} style={{ color: '#0D7377' }} />
              <span style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#0D7377',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Service
              </span>
            </div>
            <h1 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(3rem, 6vw, 5rem)',
              lineHeight: 1.1,
              color: '#C9A227',
              textTransform: 'uppercase',
              marginBottom: '32px'
            }}>
              Resolve the AI Hype.<br />Build Real AI Capability.
            </h1>
            <p style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '1.5rem',
              lineHeight: 1.6,
              color: '#ffffff'
            }}>
              Structured programmes designed to demystify AI for business leaders and their teams. Move from talking about AI to strategically implementing it in practical, high-impact areas.
            </p>
          </div>
        </div>
      </section>

      {/* What are AI Enablement Programmes - LIGHT TEAL with Chaos Animation */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.3} />
        </div>
        
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(232, 244, 245, 0.4) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(232, 244, 245, 0.3) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textTransform: 'uppercase',
                marginBottom: '32px'
              }}>
                What are AI Enablement Programmes?
              </h2>
              <div style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.8,
                color: '#0a4447'
              }}>
                <p style={{ marginBottom: '20px' }}>
                  These are structured programmes designed to build genuine AI literacy and capability within your organisation. We cut through the hype and help you understand:
                </p>
                <ul style={{
                  paddingLeft: '24px',
                  marginBottom: '20px',
                  lineHeight: 1.8
                }}>
                  <li>What AI can (and can't) actually do for your specific business</li>
                  <li>Where the highest-value opportunities lie</li>
                  <li>How to implement AI practically without needing a team of data scientists</li>
                  <li>How to measure ROI and iterate effectively</li>
                </ul>
                <p style={{ fontWeight: 600, color: '#C9A227' }}>
                  From confused to confident. From hype to strategic action.
                </p>
              </div>
            </div>
            <div style={{
              backgroundColor: '#ffffff',
              border: '2px solid #0D7377',
              borderRadius: '8px',
              padding: '48px',
              boxShadow: '0 8px 32px rgba(13, 115, 119, 0.1)'
            }}>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#0a4447',
                marginBottom: '24px',
                textTransform: 'uppercase'
              }}>
                Programme Components
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {[
                  'AI strategy workshops',
                  'Hands-on tool training',
                  'Use case identification & prioritisation',
                  'Pilot implementation support',
                  'Change management & adoption guidance'
                ].map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '1rem',
                    color: '#0a4447'
                  }}>
                    <CheckCircle2 size={24} style={{
                      color: '#0D7377',
                      flexShrink: 0,
                      marginTop: '2px'
                    }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why It's Relevant - WHITE with Logomark Pattern */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.025} density="medium" tint="teal" />
        <GradientOverlay variant="subtle" opacity={0.1} />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '32px'
          }}>
            Why SMEs & Scale-Ups Need This
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.7,
            color: '#0a4447',
            maxWidth: '900px',
            margin: '0 auto 48px'
          }}>
            Many SMEs feel left behind by the AI revolution. Our programmes provide a clear, affordable roadmap to build internal AI literacy and start leveraging AI for a competitive advantage without needing a team of data scientists or a Fortune 500 budget.
          </p>
        </div>
      </section>

      {/* Cost of Inaction - LIGHT TEAL */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.02} density="sparse" tint="gold" />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            The Cost of AI Paralysis
          </h2>
          <div style={{
            backgroundColor: '#ffffff',
            border: '2px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '8px',
            padding: '48px',
            maxWidth: '900px',
            margin: '0 auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {costsOfInaction.map((cost, index) => (
                <li key={index} style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  lineHeight: 1.6,
                  color: '#1a1c1b'
                }}>
                  <XCircle size={24} style={{
                    color: '#dc3545',
                    flexShrink: 0,
                    marginTop: '4px'
                  }} />
                  <span>{cost}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Use Cases - DARK (contrast section) */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#084144',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.1} />
        </div>
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            color: '#C9A227',
            textTransform: 'uppercase',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Real Australian B2B Success Stories
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#b8d8da',
            maxWidth: '800px',
            margin: '0 auto 80px',
            textAlign: 'center'
          }}>
            See how AI enablement is transforming businesses across Australia.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
            gap: '40px'
          }}>
            {useCases.map((useCase, index) => (
              <div key={index} style={{
                backgroundColor: 'rgba(10, 93, 96, 0.6)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(201, 162, 39, 0.3)',
                borderRadius: '8px',
                padding: '40px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#C9A227';
                e.currentTarget.style.transform = 'translateY(-8px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(201, 162, 39, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  display: 'inline-block',
                  backgroundColor: '#0D7377',
                  color: '#ffffff',
                  padding: '6px 16px',
                  borderRadius: '4px',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {useCase.location}
                </div>
                <h3 style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#C9A227',
                  marginBottom: '16px'
                }}>
                  {useCase.title}
                </h3>
                <p style={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  lineHeight: 1.7,
                  color: '#ffffff'
                }}>
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA for AI - WHITE with Logomark Pattern */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.02} density="sparse" tint="teal" />
        
        <div className="container" style={{
          maxWidth: '1000px',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3.5rem)',
            lineHeight: 1.2,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '32px'
          }}>
            Stop Talking About AI.<br />Start Using It Strategically.
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.7,
            color: '#0a4447',
            marginBottom: '48px'
          }}>
            The businesses winning with AI aren't the ones with the biggest budgets—they're the ones with the clearest strategy. Let us help you build yours.
          </p>
        </div>
      </section>

      {/* CTA */}
      <CTASection />
    </div>
  );
};

export default AIEnablement;
